// src/lib/calculator/calculations.ts
import { type SyringeSize, type CalculationResult, type ReverseProfile, type DisplayUnit } from "./types";

export function calculateConcentration(peptideMg: number, bacWaterMl: number): number {
	if (bacWaterMl <= 0) return 0;
	return peptideMg / bacWaterMl;
}

export function calculateVolume(doseMg: number, concentrationMgMl: number): number {
	if (concentrationMgMl <= 0) return 0;
	return doseMg / concentrationMgMl;
}

export function calculateInsulinUnits(volumeMl: number, calibration: 40 | 100 = 100): number {
	return volumeMl * calibration;
}

export function calculateDosesPerVial(peptideMg: number, doseMg: number): number {
	if (doseMg <= 0) return 0;
	return peptideMg / doseMg;
}

/** Formats doses-per-vial for display: up to one decimal place (no trailing ".0"). */
export function formatDosesPerVialDisplay(value: number): string {
	if (value <= 0) return "—";
	const rounded = Math.round(value * 10) / 10;
	if (Number.isInteger(rounded)) return String(rounded);
	return rounded.toFixed(1);
}

// NOT money — no style:"currency" — so the ESLint money guard does not apply.
const decimalFormatterCache = new Map<string, Intl.NumberFormat>();

export function formatDecimalDisplay(
	value: number,
	locale: string,
	digits?: { min?: number; max?: number },
): string {
	const min = digits?.min ?? 0;
	const max = Math.max(digits?.max ?? 2, min);
	const key = `${locale}|${min}|${max}`;
	let formatter = decimalFormatterCache.get(key);
	if (!formatter) {
		formatter = new Intl.NumberFormat(locale, {
			minimumFractionDigits: min,
			maximumFractionDigits: max,
		});
		decimalFormatterCache.set(key, formatter);
	}
	return formatter.format(value);
}

/**
 * The smallest stock insulin syringe (0.3 / 0.5 / 1.0 ml) that holds one draw, or
 * `null` when the draw exceeds the largest barrel we sell. `null` means "no syringe
 * fits" — callers must NOT tell the user to reach for a bigger syringe, because none
 * exists; the honest remedies are less bacteriostatic water or splitting the dose.
 */
export function recommendSyringe(volumeMl: number): SyringeSize | null {
	if (volumeMl <= 0.3) return 0.3;
	if (volumeMl <= 0.5) return 0.5;
	if (volumeMl <= 1.0) return 1.0;
	return null;
}

export function checkVolumeWarning(volumeMl: number, syringeSizeMl: number): boolean {
	return volumeMl > syringeSizeMl;
}

export function calculateAll(input: {
	peptideAmount: number;
	bacWater: number;
	dose: number;
	syringeSize: SyringeSize;
	calibration?: 40 | 100;
}): CalculationResult {
	const concentration = calculateConcentration(input.peptideAmount, input.bacWater);
	const volume = calculateVolume(input.dose, concentration);
	const insulinUnits = calculateInsulinUnits(volume, input.calibration ?? 100);
	const dosesPerVial = calculateDosesPerVial(input.peptideAmount, input.dose);
	const recommended = recommendSyringe(volume);
	const volumeExceedsSyringe = checkVolumeWarning(volume, input.syringeSize);

	return {
		concentration,
		volume,
		insulinUnits,
		dosesPerVial,
		recommendedSyringe: recommended,
		volumeExceedsSyringe,
	};
}

// ─── Reverse calculator ────────────────────────────────────────────────────────

const REVERSE_SYRINGES: { ml: SyringeSize; iu: number }[] = [
	{ ml: 0.3, iu: 30 },
	{ ml: 0.5, iu: 50 },
	{ ml: 1.0, iu: 100 },
];
const round2 = (n: number, d = 2) => {
	const f = 10 ** d;
	return Math.round(n * f) / f;
};

export interface ReverseResult {
	bac: number;
	iu: number;
	vol: number;
	syringe: SyringeSize;
	fillRatio: number;
	isFive: boolean;
	isTen: boolean;
	conc: number;
	doses: number;
}

export function calcReverse(input: {
	vial: number;
	dose: number;
	profile?: ReverseProfile;
}): ReverseResult | null {
	const { vial, dose, profile = "easier" } = input;
	if (dose <= 0 || vial <= 0) return null;
	const candidates: ReverseResult[] = [];
	for (let bac = 0.5; bac <= 5.001; bac += 0.1) {
		const b = round2(bac, 1);
		const conc = vial / b;
		const vol = dose / conc;
		const iu = vol * 100;
		const nearest = Math.round(iu);
		if (nearest <= 0) continue;
		if (Math.abs(iu - nearest) < 0.06) {
			const fitSyr = REVERSE_SYRINGES.find((s) => vol <= s.ml + 1e-6);
			if (!fitSyr) continue;
			candidates.push({
				bac: b,
				iu: nearest,
				vol: round2(vol, 3),
				syringe: fitSyr.ml,
				fillRatio: vol / fitSyr.ml,
				isFive: nearest % 5 === 0,
				isTen: nearest % 10 === 0,
				conc: 0,
				doses: 0,
			});
		}
	}
	if (!candidates.length) return null;
	let pick: ReverseResult;
	if (profile === "smaller") {
		const fives = candidates.filter((c) => c.isFive);
		const pool = fives.length ? fives : candidates;
		pick = pool.reduce((a, c) => (c.vol < a.vol ? c : a));
	} else {
		const fives = candidates.filter((c) => c.isFive && c.fillRatio >= 0.3 && c.fillRatio <= 0.85);
		const pool = fives.length ? fives : candidates.filter((c) => c.isFive);
		const base = pool.length ? pool : candidates;
		pick = base.reduce((a, c) => (c.iu > a.iu ? c : a));
	}
	return { ...pick, conc: vial / pick.bac, doses: Math.floor(vial / dose) };
}

// ─── mg/mcg sanity check ──────────────────────────────────────────────────────

export function checkMgMcg(
	doseMg: number,
	doseRangeMax: number | undefined,
): { tooHigh: true; suggestMg: number } | null {
	if (!doseMg || doseRangeMax == null) return null;
	if (doseMg > doseRangeMax * 50) return { tooHigh: true, suggestMg: round2(doseMg / 1000, 3) };
	return null;
}

// ─── Blend component breakdown ────────────────────────────────────────────────

export interface BlendComponent {
	name: string;
	mg: number;
	anchor?: boolean;
}
export interface BlendShare {
	name: string;
	anchor: boolean;
	v: number;
	u: "mg" | "mcg";
}

export function componentBreakdown(
	components: BlendComponent[] | undefined,
	doseMg: number,
): BlendShare[] | null {
	if (!components?.length || doseMg <= 0) return null;
	const total = components.reduce((s, c) => s + c.mg, 0);
	if (total <= 0) return null;
	return components.map((c) => {
		const mg = doseMg * (c.mg / total);
		return mg < 1
			? { name: c.name, anchor: !!c.anchor, v: Math.round(mg * 1000), u: "mcg" as const }
			: { name: c.name, anchor: !!c.anchor, v: round2(mg, 2), u: "mg" as const };
	});
}

// ─── Default display unit ─────────────────────────────────────────────────────

export function defaultUnitOf(category: string): DisplayUnit {
	return /GLP-1|GIP/i.test(category) ? "marks" : "iu";
}

// ─── LP Retatrutide helpers ───────────────────────────────────────────────────

/** U-100 syringe, 1 ml barrel: 100 marks is the physical maximum of one draw. */
export const LP_UNIT_MAX = 100;
/** Below 5 marks the draw is too small to read off the barrel reliably. */
export const LP_UNIT_MIN = 5;

export type UnitRangeWarning = "over_max" | "under_min" | null;

/**
 * Range check for the number of marks on the syringe.
 * `> max` → "over_max"; `< min` and `> 0` → "under_min"; otherwise null.
 * A non-finite value or 0 (no dose entered yet) is never a warning.
 */
export function checkUnitRange(units: number, range?: { min?: number; max?: number }): UnitRangeWarning {
	if (!Number.isFinite(units)) return null;
	const max = range?.max ?? LP_UNIT_MAX;
	const min = range?.min ?? LP_UNIT_MIN;
	if (units > max) return "over_max";
	if (units > 0 && units < min) return "under_min";
	return null;
}

/**
 * Smallest vial (ascending `doseMg`) that yields at least `minDoses` whole doses for the
 * requested `doseMg`. Variants without a `doseMg` are ignored; `doseMg <= 0` returns null.
 * Returns null when no vial reaches `minDoses`, deliberately NOT falling back to the
 * largest vial, because a vial with fewer than four doses is not the LP's recommendation.
 */
export function recommendVial<T extends { doseMg: number | null }>(
	variants: readonly T[],
	doseMg: number,
	minDoses = 4,
): T | null {
	if (!Number.isFinite(doseMg) || doseMg <= 0) return null;
	const dosed = variants
		.filter((v): v is T & { doseMg: number } => typeof v.doseMg === "number" && Number.isFinite(v.doseMg))
		.sort((a, b) => a.doseMg - b.doseMg);
	return dosed.find((v) => Math.floor(v.doseMg / doseMg) >= minDoses) ?? null;
}

/**
 * Price of a single dose: `unitPrice / dosesPerVial`, unrounded (callers format).
 * Null when `dosesPerVial <= 0` or either input is not finite.
 */
export function pricePerDose(unitPrice: number, dosesPerVial: number): number | null {
	if (!Number.isFinite(unitPrice) || !Number.isFinite(dosesPerVial)) return null;
	if (dosesPerVial <= 0) return null;
	return unitPrice / dosesPerVial;
}
